/**
 * Type-only imports, for their `declare module` augmentation ALONE: `insertTable` and
 * `toggleTaskList` are contributed to `ChainedCommands` by these packages, and TypeScript only
 * sees the augmentation in a file that names the module. No runtime import — the extensions
 * themselves are registered by `DocumentEditor`.
 */
import type {} from "@tiptap/extension-list";
import type {} from "@tiptap/extension-table";
import type { Editor } from "@tiptap/react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { blockMenuEntries, filterBlockEntries } from "~/features/documents/editor/blockMenuEntries";
import {
  bumpThinkerRecency,
  type MentionCandidate,
  orderMentionCandidates,
  rankThinkers,
} from "~/features/documents/editor/mentionRecency";
import { type SuggestionItem, SuggestionMenu } from "~/features/documents/editor/SuggestionMenu";
import type { SuggestionState } from "~/features/documents/editor/suggestionTrigger";

/**
 * The two menus a document offers while typing: `/` inserts a block, `@` mentions somebody.
 *
 * Both replace the trigger range in ONE transaction — the trigger text and its character go
 * away with the insertion, never in a second edit that could be undone separately from what it
 * produced.
 */

export interface MentionSources {
  /** The whole workspace registry; filtering and ranking are client-side by design. */
  thinkers: Array<{ id: string; name: string; description?: string }>;
  documents: Array<{ id: string; title: string }>;
  people: Array<{ id: string; name: string }>;
}

export function BlockSuggestionMenu({
  editor,
  state,
  onDismiss,
  onInsertImage,
}: {
  editor: Editor | null;
  state: SuggestionState;
  onDismiss: () => void;
  /**
   * W10 §10 — the Image entry asks the HOST for a file, because uploading one needs a document
   * id and an API client, neither of which an editor extension has. Omitted, and the entry is
   * still listed but does nothing on selection — so the host that wants images supplies it.
   */
  onInsertImage?: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  const entries = useMemo(() => blockMenuEntries(), []);
  const filtered = useMemo(() => filterBlockEntries(entries, state.query), [entries, state.query]);

  const items: SuggestionItem[] = filtered.map((entry) => ({
    id: entry.id,
    // W10 §10 — the titles are declarations in `blockMenuEntries.ts` and the widget registry, so
    // the parser cannot see them; they are declared in `slate/src/i18nGuards/dynamicKeys.ts`
    // instead. `t()` on a key with no catalogue entry returns the key, so a widget label that
    // has not been translated yet still reads as itself rather than as an empty row.
    title: t(entry.title),
    section: entry.group === "widget" ? t("Widgets") : t("Blocks"),
  }));

  const onSelect = useCallback(
    (item: SuggestionItem) => {
      const entry = filtered.find((candidate) => candidate.id === item.id);
      if (!editor || !entry) {
        return;
      }
      // W10 §10 — a command entry (table / task list / image) cannot be an `insertContent`:
      // an empty table is an invalid node, a task list is a toggle, and an image has no `src`
      // until a file is chosen. The `/query` is still removed in the SAME chain either way.
      const chain = editor.chain().focus().deleteRange({ from: state.from, to: state.to });
      if (entry.command === "table") {
        chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
      } else if (entry.command === "taskList") {
        chain.toggleTaskList().run();
      } else if (entry.command === "image") {
        chain.run();
        // Choosing a file is the host's business (it owns the upload); the menu only asks.
        onInsertImage?.();
      } else if (entry.node) {
        chain.insertContent({ type: entry.node.type, attrs: entry.node.attrs }).run();
      }
      onDismiss();
    },
    [editor, filtered, onDismiss, onInsertImage, state.from, state.to],
  );

  return (
    <SuggestionMenu
      state={state}
      items={items}
      emptyLabel={t("No blocks match")}
      onSelect={onSelect}
      onDismiss={onDismiss}
    />
  );
}

/**
 * `@` — thinkers first, then people, then documents.
 *
 * The ordering is the wave's addition and it is deliberate: in a document being worked on, the
 * name you most often want is the thinker you are handing the work to. Recency is per viewer
 * and per device, stored in this browser, because no endpoint holds it and inventing one for
 * somebody's habit would be worse than saying so.
 */
export function MentionSuggestionMenu({
  editor,
  state,
  sources,
  onDismiss,
}: {
  editor: Editor | null;
  state: SuggestionState;
  sources: MentionSources;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();

  const candidates = useMemo<MentionCandidate[]>(() => {
    const needle = state.query.trim().toLowerCase();
    const thinkers = rankThinkers(sources.thinkers, state.query).map((thinker) => ({
      kind: "thinker" as const,
      id: `thinker:${thinker.id}`,
      label: thinker.name,
      modelId: thinker.id,
    }));
    const people = sources.people
      .filter((person) => (needle ? person.name.toLowerCase().includes(needle) : true))
      .slice(0, 5)
      .map((person) => ({
        kind: "user" as const,
        id: `user:${person.id}`,
        label: person.name,
        modelId: person.id,
      }));
    const documents = sources.documents
      .filter((doc) => (needle ? doc.title.toLowerCase().includes(needle) : true))
      .slice(0, 5)
      .map((doc) => ({
        kind: "document" as const,
        id: `document:${doc.id}`,
        label: doc.title,
        modelId: doc.id,
      }));
    return orderMentionCandidates([...thinkers, ...people, ...documents]);
  }, [sources, state.query]);

  const sectionOf = (kind: MentionCandidate["kind"]): string =>
    kind === "thinker" ? t("Thinkers") : kind === "user" ? t("People") : t("Documents");

  const items: SuggestionItem[] = candidates.map((candidate) => ({
    id: candidate.id,
    title: candidate.label,
    section: sectionOf(candidate.kind),
  }));

  const onSelect = useCallback(
    (item: SuggestionItem) => {
      const candidate = candidates.find((entry) => entry.id === item.id);
      if (!editor || !candidate) {
        return;
      }
      if (candidate.kind === "thinker") {
        // Mentioning a thinker runs NO membership, permission or invite work — it writes a
        // name. The only side effect is this viewer's own ordering.
        bumpThinkerRecency(candidate.modelId);
      }
      editor
        .chain()
        .focus()
        .deleteRange({ from: state.from, to: state.to })
        .insertContent([
          {
            type: "mention",
            attrs: {
              mentionId: freshMentionId(),
              type: candidate.kind,
              modelId: candidate.modelId,
              label: candidate.label,
            },
          },
          { type: "text", text: " " },
        ])
        .run();
      onDismiss();
    },
    [candidates, editor, onDismiss, state.from, state.to],
  );

  return (
    <SuggestionMenu
      state={state}
      items={items}
      emptyLabel={t("Nobody matches")}
      onSelect={onSelect}
      onDismiss={onDismiss}
    />
  );
}

let mentionCounter = 0;

/** Identity of the MENTION, not of the thing mentioned — two mentions of one person differ. */
function freshMentionId(): string {
  const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
  if (typeof cryptoRef?.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  mentionCounter += 1;
  return `mention-${Date.now().toString(36)}-${mentionCounter.toString(36)}`;
}
